import axios, { type AxiosInstance } from 'axios'

const EXEMPT_URLS = ['/accept-invite']

export default defineNuxtPlugin({
  name: 'axios',

  parallel: true,

  dependsOn: ['store', 'toast', 'keycloak'],

  setup({ $config, _route, $toast, $store, $keycloak }) {
    const axiosInstance: AxiosInstance = axios.create({
      baseURL: $config.public.baseApi,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })

    axiosInstance.interceptors.request.use(async (config) => {
      const subdomainPrefix = $store.companies.subdomain

      // Compute the new base URL if subdomainPrefix is available.
      config.baseURL = subdomainPrefix
        ? $config.public.baseApi.replace('//', `//${subdomainPrefix}.`)
        : $config.public.baseApi

      if (EXEMPT_URLS.some(url => config.url?.includes(url))) {
        return config
      }

      const { $keycloakReadyPromise } = useNuxtApp()
      // Wait for keycloak to finish init
      await $keycloakReadyPromise

      if (!$keycloak.authenticated) {
        return Promise.reject({
          response: {
            status: 401,
            statusText: 'Unauthorized',
          },
        })
      }

      if ($keycloak.isTokenExpired()) {
        await $keycloak.refresh()
      }

      if ($store.cancelToken.cancelTokenSource) {
        config.cancelToken = $store.cancelToken.cancelTokenSource.token
      }

      config.headers.Authorization = `Bearer ${$keycloak.token}`
      return config
    })

    axiosInstance.interceptors.response.use(
      response => response,
      async (error) => {
        if (!error || !error.response) return Promise.reject(new Error('No response from our APIs, please check your configuration carefully!'))
        const { response: errorResponse } = error
        const { status, data } = errorResponse

        // Case unauthorized
        if (status === 401) {
          return $keycloak.logoutUser().then(async () => {
            return await navigateTo('/login')
          })
        }

        // Case missing permissions
        if (status === 403 && _route.name !== 'login') {
          return await navigateTo({
            path: '/errors/ErrorPermission',
            query: {
              message:
                  'The page you are trying to access has restricted access. Please contact your administrator for more permission.',
              from: data?.detail?.includes(
                'You do not have permission to perform this action.',
              )
                ? 'middleware'
                : '403',
            },
          })
        }

        // Case all other user generated/server errors
        let errorMessage = ''

        if (status > 399 || status < 500) {
          // TODO: Extract error for all statuses
          errorMessage
              = data?.message
              ?? data?.detail
              ?? errorResponse.statusText
              ?? 'An error has occured!'
          if (status === 400 && data) {
            // Check whether it's an array of objects or just one object
            if (Array.isArray(data)) {
              const firstErrorObject = data[0]
              const firstErrorKey = Object.keys(firstErrorObject)[0]
              errorMessage = `${firstErrorKey} - ${firstErrorObject[firstErrorKey]}`
            }
            else {
              const responseObject = data
              const responseKeys = Object.keys(responseObject)
              errorMessage = typeof responseObject[responseKeys[0]] === 'string' ? responseObject[responseKeys[0]] : responseObject[responseKeys[0]][0]
            }
          }
        }
        if (status === 500) {
          errorMessage
              = 'An error occured in our servers, please try again later!'
        }

        $toast.error(errorMessage, {
          timeout: 5000,
          position: 'top-right',
        })

        return Promise.reject(error)
      },
    )

    return {
      provide: {
        axios: axiosInstance,
      },
    }
  },
})
